import { ForwardIcon, HeartIcon, MessageSquareTextIcon } from 'lucide-react';

import type { Post } from '@/entities/post/types';
import { ProfileImage } from '@/shared/components/profile-image';
import { TikTokLogo } from '@/shared/icons/index';
import { formatRelativeTime } from '@/shared/utils/format-relative-time';

interface Props {
  post: Post;
}

export function PreviewPostCard({ post }: Props) {
  const postTime = formatRelativeTime(post.date);
  const updatedTime = formatRelativeTime(post.scraped_at);

  return (
    <div className="border-border bg-background relative rounded-lg border shadow-sm">
      <div className="p-4">
        <div className="flex gap-4">
          {/* Post Content */}
          <div className="flex-1">
            {/* User Info */}
            <div className="mb-4 flex items-center gap-3">
              <ProfileImage
                src={post.author.profile_image_url}
                name={post.author.nickname}
                className="bg-muted size-10 rounded-full object-cover"
              />
              <div className="flex-1">
                <div className="text-card-foreground font-semibold">{post.author.username}</div>
                <div className="text-muted-foreground flex items-center gap-1 text-sm">
                  <span>{post.author.nickname}</span>
                  <span>·</span>
                  <span>{postTime}</span>
                </div>
              </div>
            </div>

            {/* Post Text */}
            <div className="mb-4">
              <p className="text-primary line-clamp-[8] text-sm leading-5">
                {post.extraction.content}
                {post.extraction.hashtags.length > 0 && (
                  <span className="text-primary">
                    {' '}
                    {post.extraction.hashtags.map((tag) => `#${tag}`).join(' ')}
                  </span>
                )}
              </p>
            </div>

            {/* Post Stats */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1">
                <HeartIcon className="text-muted-foreground size-4" />
                <span className="text-muted-foreground text-sm">
                  {post.extraction.engagement.like.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <MessageSquareTextIcon className="text-muted-foreground size-4" />
                <span className="text-muted-foreground text-sm">
                  {post.extraction.engagement.comment.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <ForwardIcon className="text-muted-foreground size-4" />
                <span className="text-muted-foreground text-sm">
                  {post.extraction.engagement.shared.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Image/Video */}
          {post.extraction.video && (
            <div className="bg-muted aspect-720/1280 w-[140px] shrink-0 self-stretch overflow-hidden rounded-md">
              <img
                src={post.extraction.video.poster_url}
                alt="Post video thumbnail"
                className="size-full object-cover"
              />
            </div>
          )}
        </div>
      </div>

      {/* Post Footer */}
      <div className="border-border border-t px-4 py-2">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">Uploaded {updatedTime}</span>
          <TikTokLogo className="text-muted-foreground size-4" />
        </div>
      </div>
    </div>
  );
}
